import { useEffect, type RefObject } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { handleClick, handleHover, setFloorplanDimensions, setSvgInventory } from './actions'
import { STYLES } from './styles'
import type { Inventory, SvgInventoryByDate } from './types'
import type nlBookingApp from '../reducers/combineReducers'

interface InteractiveFloorplanProps {
  availableInventory: Inventory[]
  soldInventory: Inventory[]
  boundToggleInventoryItem: (inventoryId: string) => void
  selectedDate: string
  svgRef: RefObject<HTMLDivElement>
  svg: string
  svgInventory: SvgInventoryByDate
  tieredEvents?: string[]
  onSvgInit: (element: Element) => void
}
export type RootState = ReturnType<typeof nlBookingApp>

export const InteractiveFloorplan = ({
  availableInventory,
  soldInventory,
  boundToggleInventoryItem,
  selectedDate,
  svgRef,
  svg,
  svgInventory,
  tieredEvents,
  onSvgInit,
}: InteractiveFloorplanProps) => {
  const dispatch = useDispatch()
  const featureFlags = useSelector((state: RootState) => state.featureFlags)

  useEffect(() => {
    if (!svgRef.current) {
      return
    }
    const svgContainer = svgRef.current
    svgContainer.querySelector('title')?.remove() // svg edit adds placeholder title

    // by default set all shapes to unavailable
    svgContainer
      .querySelectorAll('path,rect,ellipse,circle,polyline,polygon')
      .forEach(element => element.setAttribute('style', STYLES.unavailable))
    const initialInventory = availableInventory
      .filter(({ inventoryId }) => !tieredEvents?.includes(inventoryId))
      .map(({ inventoryId, inventoryName, inventoryFloorplanInventoryClassname }) => {
        // we use the inventory names as area selectors (they map to the class names in the svg)
        const selectorClass =
          featureFlags.useAdminPanelFloorplanImages && inventoryFloorplanInventoryClassname
            ? `.${inventoryFloorplanInventoryClassname
                .split(' ')
                .map(classname => CSS.escape(classname))
                .join(',')}`
            : `.${CSS.escape(inventoryName.toLowerCase().replaceAll(' ', '-'))}` // Pool Area => .pool-area
        const elements = [...svgContainer.querySelectorAll(selectorClass)].map(element => {
          element.addEventListener('mouseover', () => dispatch(handleHover({ inventoryName, hover: true, selectedDate })))
          element.addEventListener('mouseout', () => dispatch(handleHover({ inventoryName, hover: false, selectedDate })))
          element.addEventListener('click', () => {
            boundToggleInventoryItem(inventoryId)
            dispatch(handleClick({ inventoryName, selectedDate }))
          })
          return element
        })
        return {
          elements,
          inventoryId,
          inventoryName,
          status: svgInventory[selectedDate]?.find(({ inventoryName: svgInvName }) => svgInvName === inventoryName)?.status || 'default',
          isActive: true,
        } as const
      })
    const firstElement = initialInventory.find(({ elements }) => elements.length)?.elements[0]
    if (firstElement) {
      onSvgInit(firstElement)
    }

    dispatch(setFloorplanDimensions({ height: svgContainer.offsetHeight, width: svgContainer.offsetWidth }))
    dispatch(setSvgInventory({ [selectedDate]: initialInventory }))

    // careful to only set inventory state/handlers once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const svgInventoryBySelectedDate = svgInventory[selectedDate]
  useEffect(() => {
    soldInventory.forEach(({ inventoryName, inventoryFloorplanInventoryClassname }) => {
      const selectorClass =
        featureFlags.useAdminPanelFloorplanImages && inventoryFloorplanInventoryClassname
          ? `.${inventoryFloorplanInventoryClassname
              .split(' ')
              .map(classname => CSS.escape(classname))
              .join(',')}`
          : `.${CSS.escape(inventoryName.toLowerCase().replaceAll(' ', '-'))}`
      svgRef.current?.querySelectorAll(selectorClass).forEach(element => element.setAttribute('style', STYLES.soldOut))
    })
    svgInventoryBySelectedDate?.forEach(({ elements, status }) => {
      switch (status) {
        case 'hover':
          elements.forEach(el => el.setAttribute('style', STYLES.hover))
          break
        case 'click':
          elements.forEach(el => el.setAttribute('style', STYLES.click))
          break
        default:
          elements.forEach(el => el.setAttribute('style', STYLES.available))
      }
    })
  }, [soldInventory, svgInventoryBySelectedDate, featureFlags.useAdminPanelFloorplanImages, svgRef])

  return <SvgContainer ref={svgRef} dangerouslySetInnerHTML={{ __html: svg }} />
}

const SvgContainer = styled.div`
  & > svg {
    display: block;
  }
`
